import {DatePipe} from '@angular/common';
import {Component, EventEmitter, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import flatMap from 'lodash-es/flatMap';
import {BehaviorSubject, combineLatest, Observable, of, Subject, Subscription} from 'rxjs';
import {catchError, filter, map, pluck, switchMap, tap} from 'rxjs/operators';
import {TimecardStatus} from '../../../model';
import {cache} from '../../../utils';
import {AnalyticsService} from '../../shared/analytics.service';
import {HttpError} from '../../shared/api.service';
import {AppStateService} from '../../shared/app-state.service';
import {AuthService} from '../../shared/auth.service';
import {FirstNamePipe} from '../../shared/first-name.pipe';
import {NameFormatPipe} from '../../shared/name-format.pipe';
import {NavigationService} from '../../shared/navigation.service';
import {ClientApiService} from '../client-api.service';
import {TermsModalComponent} from '../terms-modal/terms-modal.component';
import {UpdateAssignmentModalComponent} from '../update-assignment-modal/update-assignment-modal.component';
import {environment} from '../../../environments/environment';

@Component({
  selector: 'position-details-page',
  templateUrl: './position-details-page.component.html',
  styleUrls: ['./position-details-page.component.scss']
})
export class PositionDetailsPageComponent implements OnInit, OnDestroy {

  @ViewChild('termsModal') termsModal: TermsModalComponent;
  @ViewChild('updateAssignmentModal') updateAssignmentModal: UpdateAssignmentModalComponent;

  TimecardStatus = TimecardStatus;

  order$: Observable<Order>;
  talent$: Observable<Person>;
  staff$: Observable<StaffInfo[]>;
  displayId$: Observable<number>;

  timecardsReload$ = new Subject<void>();
  timecardsLoading$ = new BehaviorSubject(true);
  timecards$: Observable<Timecard[]>;
  reviewsLoading$ = new BehaviorSubject(true);
  reviews$: Observable<QccReply[]>;
  historyLoading$: Observable<boolean>;
  history$: Observable<HistoricalEvent[]>;
  profileId$: Observable<string>;
  futureStartDate$: Observable<boolean>;
  profileReturnTo$: Observable<string>;
  returnTo$ = this.route.queryParams.pipe(pluck('returnTo'));
  returnToHome$ = this.returnTo$.pipe(map((returnTo: string) => returnTo.startsWith('home')));
  parentReturnTo$ = this.returnTo$.pipe(map((returnTo: string) => returnTo.split(',').slice(0, -1).join(',')));
  fromTracker$ = this.returnTo$.pipe(map((returnTo: string) => returnTo.endsWith('candidates')));
  masquerading$ = this.Auth.masquerading$;
  endOfAssignment = environment.flags.endOfAssignment;

  private timecardsSub: Subscription;
  private reviewsSub: Subscription;

  constructor(private route: ActivatedRoute,
              private Navigation: NavigationService,
              private Analytics: AnalyticsService,
              private Api: ClientApiService,
              private Auth: AuthService,
              private AppState: AppStateService,
              private DatePipe: DatePipe) {
  }

  ngOnInit() {
    this.AppState.loading$.next(true);
    this.displayId$ = (this.route.params.pipe(pluck('id'))as Observable<string>)
      .pipe(map(id => AnalyticsService.deobfuscateId(id)));
    this.order$ = this.route.paramMap
      .pipe(
        tap(() => this.AppState.loading$.next(true)),
        switchMap(params => this.Api.getOrder(params.get('id'))),
        tap(() => this.AppState.loading$.next(false)),
        cache()
      );
    this.talent$ = this.order$.pipe(map(o => o.talent));

    this.timecards$ = combineLatest(
      this.route.params.pipe(pluck('id')) as Observable<string>,
      this.timecardsReload$
    )
      .pipe(
        tap(() => this.timecardsLoading$.next(true)),
        switchMap(([id, _]) => this.Api.getTimecards(id)),
        tap(() => this.timecardsLoading$.next(false)),
        catchError((err: HttpError) =>
          this.handleError(err, 'Failed to load timecards')),
        cache()
      );

    this.reviews$ = this.route.paramMap
      .pipe(
        tap(() => this.reviewsLoading$.next(true)),
        switchMap(params => this.Api.getPositionReviews(params.get('id'))),
        tap(() => this.reviewsLoading$.next(false)),
        catchError((err: HttpError) =>
          this.handleError(err, 'Failed to load reviews.')),
        cache()
      );

    this.staff$ = this.Auth.user$.pipe(filter(v => !!v), pluck('staff'));
    this.profileId$ = this.order$.pipe(pluck('latestTalentSubmittalProfileId'));
    this.futureStartDate$ = this.order$.pipe(pluck('startDate'), map(date => date > new Date()));
    this.setupProfileReturnTo();
    this.setupHistoryLoading();
    this.setupHistory();

    // Trigger initial HTTP requests
    this.reviewsSub = this.reviews$.subscribe();
    this.timecardsSub = this.timecards$.subscribe();
    this.timecardsReload$.next();
  }

  setupHistoryLoading() {
    this.historyLoading$ = combineLatest(
      this.timecardsLoading$,
      this.reviewsLoading$,
      (timecardsLoading, reviewsLoading) => timecardsLoading || reviewsLoading);
  }

  setupHistory() {
    this.history$ = combineLatest(
      this.timecards$,
      this.reviews$,
      this.talent$,
      this.AppState.locale$,
      (timecards, reviews, talent, locale) => {
        const talentFirstName = FirstNamePipe.apply(NameFormatPipe.apply(talent, locale), locale);
        const timecardEvents = this.extractTimecardEvents(timecards, talentFirstName);
        const reviewEvents = this.extractReviewEvents(reviews, talentFirstName);

        return [...timecardEvents, ...reviewEvents].sort(historicalEventComparator);
      });
  }

  setupProfileReturnTo() {
    this.profileReturnTo$ = combineLatest(
      this.returnTo$,
      this.futureStartDate$,
      this.order$,
      (returnTo: string, futureStartDate: boolean) => {
        const returnToList = returnTo.split(',');
        returnToList.push('working-profile');

        return (futureStartDate || returnTo.includes('candidates')) ?
          returnToList.join(',') :
          returnToList.join(',').replace('positions', 'working-positions');
      }
    );
  }

  extractTimecardEvents(timecards: Timecard[], name: string): HistoricalEvent[] {
    let unsignedCount = 0;

    const events = flatMap(timecards, timecard => {
      const signedAndApproved = [];

      if (timecard.statusId === TimecardStatus.Submitted) {
        ++unsignedCount;
      } else {
        if (timecard.approved) {
          signedAndApproved.push(this.getApprovedEvent(timecard));
        }
        if (timecard.signed) {
          signedAndApproved.push(this.getSignedEvent(timecard, name));
        }
      }

      return signedAndApproved;
    });

    return (unsignedCount > 0) ? [this.getUnsignedEvent(unsignedCount, name), ...events] : events;
  }

  getSignedEvent(timecard: Timecard, name: string): HistoricalEvent {
    return {
      alert: false,
      text: 'position-details.history.signed',
      textParams: {
        name,
        date: this.DatePipe.transform(timecard.signed, 'mediumDate')
      },
      date: timecard.signed
    };
  }

  getApprovedEvent(timecard: Timecard): HistoricalEvent {
    return {
      alert: false,
      text: 'position-details.history.approved',
      textParams: {
        weekStart: this.DatePipe.transform(timecard.weekStartingMonday, 'mediumDate'),
        weekEnd: this.DatePipe.transform(timecard.weekEndingSunday, 'mediumDate')
      },
      date: timecard.approved
    };
  }

  getUnsignedEvent(unsignedCount: number, name: string): HistoricalEvent {
    return {
      alert: true,
      text: 'position-details.history.unsigned',
      textParams: {unsignedCount, name},
      date: new Date()
    };
  }

  extractReviewEvents(reviews: QccReply[], name: string): HistoricalEvent[] {
    return reviews.map(review => ({
      alert: false,
      text: 'position-details.history.review',
      textParams: {
        name,
        date: this.DatePipe.transform(review.date, 'mediumDate')
      },
      date: review.date
    }));
  }

  ngOnDestroy() {
    if (this.timecardsSub) this.timecardsSub.unsubscribe();
    if (this.reviewsSub) this.reviewsSub.unsubscribe();
  }

  showTermsModal(event: Event, timecard: Timecard) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    this.termsModal.show([timecard.timecardId]);
  }

  signTimecardComplete() {
    this.timecardsReload$.next();
  }

  handleError(err: HttpError, message: string) {
    console.error(message, err);
    this.Navigation.handleError(err);
    return of(null);
  }

  editEndDate(event: Event) {
    event.stopPropagation();
    this.updateAssignmentModal.show('proactive');
  }

  onAssignmentUpdated() {
    this.Analytics.trackEvent('Positions Detail: Assignment proactively updated');
  }
}

function historicalEventComparator(e1: HistoricalEvent, e2: HistoricalEvent) {
  if (e1.alert) {
    return -1;
  } else if (e2.alert) {
    return 1;
  }

  return e2.date.getTime() - e1.date.getTime();
}

interface HistoricalEvent {
  alert: boolean;
  text: string;
  textParams: object;
  date: Date;
}
